import { useEffect, useRef } from 'react';
import { Box } from '@zoopla-eng/zds-components/box';
import { ChevronRightSmall } from '@zoopla-eng/zds-components/icons';
import { LinkWithIcon } from '@zoopla-eng/zds-components/link';
import { Bulleted } from '@zoopla-eng/zds-components/lists/bulleted';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

import { titleStyle } from './ConfirmationStep.styles.css';
import { trackSegmentAndGAEvent } from '@zoopla/tracking';
import { StepProps } from '../../AuthModal.types';

export type ConfirmationStepProps = {
    type: 'DECLARED' | 'DERIVED';
    property?: {
        uprn: string;
        address: string;
    };
    title?: string;
} & Omit<StepProps, 'onStepCompleted'>;

const getData = ({
    property,
    type
}: Pick<ConfirmationStepProps, 'property' | 'type'>) => {
    const href = property
        ? `/agent-matcher/?uprn=${property.uprn}`
        : '/valuation/';

    // default to derived with no property
    let title =
        "If you're planning to sell, why not get an in-person valuation in the diary and see how much your home is worth.";

    if (property) {
        if (type === 'DECLARED') {
            title = `You told us you're planning to sell ${property.address}, so why not get a local agent valuation.`;
        } else {
            title = `If you're looking to sell ${property.address}, why not get a local agent valuation.`;
        }
        // no property but declared
    } else if (type === 'DECLARED') {
        title =
            "You told us you're planning to sell, so now's the time to book an in-person valuation.";
    }

    return {
        title,
        href
    };
};

export const ConfirmationStep = ({
    property,
    type,
    title: stepTitle,
    trackingData
}: ConfirmationStepProps) => {
    const stepTitleRef = useRef<HTMLHeadingElement>(null);
    const { title, href } = getData({ property, type });

    useEffect(() => {
        stepTitleRef.current?.focus();

        trackSegmentAndGAEvent(
            'Module Shown',
            {
                eventCategory: 'next_best_action',
                eventAction: 'module_shown',
                eventLabel: 'vendor_lead'
            },
            trackingData.segmentData.page
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onLinkClick = () => {
        trackSegmentAndGAEvent(
            'Module Interaction',
            {
                eventCategory: 'next_best_action',
                eventAction: 'click',
                eventLabel: 'vendor_lead'
            },
            trackingData.segmentData.page
        );
    };

    const textComponent = property ? (
        <Bulleted
            items={[
                <>Browse the top performing agents in your area</>,
                <>
                    Book an in-person valuation and take advantage of their
                    local knowledge
                </>,
                <>Sell your home quickly and easily</>
            ]}
        />
    ) : (
        <p>
            You can take advantage of your local agents' knowledge, show off
            your property's unique features, and get the most accurate
            valuation.
        </p>
    );
    const linkComponent = (
        <LinkWithIcon
            href={href}
            onClick={onLinkClick}
            iconSuffix={<ChevronRightSmall />}
        >
            {property ? 'Start your booking now' : 'Search local agents'}
        </LinkWithIcon>
    );

    return (
        <>
            {stepTitle && (
                <Box pb="S400">
                    <h1
                        className={fontGroups.mug}
                        tabIndex={-1}
                        ref={stepTitleRef}
                    >
                        {stepTitle}
                    </h1>
                </Box>
            )}
            <Box pb="S500">
                <p className={`${fontGroups.ladle} ${titleStyle}`}>{title}</p>
            </Box>
            <Box pb="S500">{textComponent}</Box>
            {linkComponent}
        </>
    );
};
